import {
  Indicator_Type_Enum,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalForm } from 'src/components/form/form/ModalForm';
import {
  useInsertIndicatorResult,
  useUpdateIndicatorResult,
} from 'src/hooks/mutations';
import { useGetIndicatorResultById } from 'src/hooks/queries';
import type { IndicatorResultFormFields } from 'src/pages/indicators/forms/indicatorResultSchema';
import {
  defaultValues,
  indicatorResultSchema,
} from 'src/pages/indicators/forms/indicatorResultSchema';
import type { ObjectWithContributors } from 'src/rbac/Permission';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { useFileUpdate } from '@/hooks/mutations/file/useFileUpdate';
import { MUTATION_NO_OP } from '@/hooks/useMutationResultNotification';
import type { CreatedEntityDestination } from '@/utils/createWithFiles';
import { saveFilesForOptionalCreatedEntity } from '@/utils/createWithFiles';
import { indicatorDetailsUrl } from '@/utils/urls';

import type { IndicatorThresholds } from '../calculateConformanceRating';
import IndicatorResultsForm from '../forms/IndicatorResultsForm';

type Props = {
  onDismiss: (saved: boolean) => void;
  parentIndicatorId: string;
  parentIndicatorType: Indicator_Type_Enum;
  Id?: string;
  indicator: ObjectWithContributors;
  parentIndicator: IndicatorThresholds;
};

const IndicatorResultModel: FC<Props> = ({
  onDismiss,
  parentIndicatorId,
  Id,
  parentIndicatorType,
  indicator,
  parentIndicator,
}) => {
  const { t } = useTranslation('common');
  const { updateFiles } = useFileUpdate();
  const { insertIndicatorResult } = useInsertIndicatorResult();
  const { updateIndicatorResult } = useUpdateIndicatorResult();
  // Resume file retries, but never carry an ID or late async result across parents.
  const createdIndicatorResultRef = useRef<
    { parentIndicatorId: string; indicatorResultId: string } | undefined
  >(undefined);
  const currentParentIndicatorIdRef = useRef<string | undefined>(
    parentIndicatorId
  );
  currentParentIndicatorIdRef.current = parentIndicatorId;
  useEffect(() => {
    currentParentIndicatorIdRef.current = parentIndicatorId;

    return () => {
      if (currentParentIndicatorIdRef.current === parentIndicatorId) {
        currentParentIndicatorIdRef.current = undefined;
      }
    };
  }, [parentIndicatorId]);
  if (
    createdIndicatorResultRef.current?.parentIndicatorId !== parentIndicatorId
  ) {
    createdIndicatorResultRef.current = undefined;
  }
  const { data, loading, error } = useGetIndicatorResultById({
    queryArgs: { id: Id ?? '' },
    shouldSkip: !Id,
  });
  if (error) {
    throw error;
  }

  const { hasPermission: userCanEdit, loading: userCanEditLoading } =
    useHasPermissionQuery('update:indicator_result', indicator);
  const { hasPermission: userCanCreate, loading: userCanCreateLoading } =
    useHasPermissionQuery('insert:indicator_result', indicator);
  const indicatorResult = data?.indicator_result[0];
  const userCanModify = indicatorResult
    ? userCanEdit && !userCanEditLoading
    : userCanCreate && !userCanCreateLoading;

  const onSave = async (data: IndicatorResultFormFields) => {
    const saveParentIndicatorId = parentIndicatorId;
    try {
      const { files, ...rest } = data;
      const retainedIndicatorResult =
        createdIndicatorResultRef.current?.parentIndicatorId ===
        saveParentIndicatorId
          ? createdIndicatorResultRef.current
          : undefined;
      let indicatorResultId =
        indicatorResult?.Id ?? retainedIndicatorResult?.indicatorResultId;
      let createdEntity: CreatedEntityDestination | undefined =
        !indicatorResult && indicatorResultId
          ? {
              id: indicatorResultId,
              destination: indicatorDetailsUrl(saveParentIndicatorId),
            }
          : undefined;
      if (indicatorResult) {
        const updatePayload = {
          ...rest,
          id: indicatorResult.Id,
          CustomAttributeData: data.CustomAttributeData || undefined,
        };
        // Ensure only relevant target value is sent based on indicator type
        // See https://linear.app/risksmart/issue/RSP-3902/updating-indicator-results-sends-invalid-mixed-data-for-text-and
        if (indicatorResult.parent?.Type === Indicator_Type_Enum.Number) {
          updatePayload.TargetValueTxt = null;
        }
        if (indicatorResult.parent?.Type === Indicator_Type_Enum.Text) {
          updatePayload.TargetValueNum = null;
        }
        await updateIndicatorResult(updatePayload);
      } else if (!indicatorResultId) {
        const insertPayload = {
          ...rest,
          IndicatorId: saveParentIndicatorId,
          CustomAttributeData: data.CustomAttributeData || undefined,
        };
        if (parentIndicatorType === Indicator_Type_Enum.Number) {
          insertPayload.TargetValueTxt = null;
        }
        if (parentIndicatorType === Indicator_Type_Enum.Text) {
          insertPayload.TargetValueNum = null;
        }
        const result = await insertIndicatorResult(insertPayload);
        indicatorResultId = result.insert_indicator_result_one?.Id;
        if (indicatorResultId) {
          if (currentParentIndicatorIdRef.current === saveParentIndicatorId) {
            createdIndicatorResultRef.current = {
              parentIndicatorId: saveParentIndicatorId,
              indicatorResultId,
            };
          }
          createdEntity = {
            id: indicatorResultId,
            destination: indicatorDetailsUrl(saveParentIndicatorId),
          };
        }
      }
      if (!indicatorResultId) {
        throw new Error('indicatorResultId is missing');
      }
      const saveFiles = () =>
        updateFiles({
          parentType: Parent_Type_Enum.IndicatorResult,
          parentId: indicatorResultId,
          originalFiles: indicatorResult?.files.map((f) => f.file) ?? [],
          selectedFiles: files,
        });
      await saveFilesForOptionalCreatedEntity({ createdEntity, saveFiles });

      if (currentParentIndicatorIdRef.current !== saveParentIndicatorId) {
        return MUTATION_NO_OP;
      }

      return { savedIds: [indicatorResultId], createdEntity };
    } catch (error) {
      if (currentParentIndicatorIdRef.current !== saveParentIndicatorId) {
        return MUTATION_NO_OP;
      }
      throw error;
    }
  };

  const handleDismiss = (saved: boolean) => {
    if (currentParentIndicatorIdRef.current === parentIndicatorId) {
      onDismiss(saved);
    }
  };

  if (loading) {
    return null;
  }
  const formId = 'indicator-result-form';

  return (
    <ModalForm
      i18n={t('indicator_results')}
      values={
        indicatorResult
          ? {
              ...defaultValues,
              ...indicatorResult,
              files: indicatorResult?.files.map((f) => f.file) ?? [],
            }
          : undefined
      }
      defaultValues={defaultValues}
      schema={indicatorResultSchema}
      onSave={onSave}
      onDismiss={handleDismiss}
      formId={formId}
      visible={true}
      readOnly={!userCanModify}
      parentType={Parent_Type_Enum.IndicatorResult}
    >
      <IndicatorResultsForm
        indicatorType={parentIndicatorType}
        readOnly={!userCanModify}
        parentIndicator={parentIndicator}
      />
    </ModalForm>
  );
};

export default IndicatorResultModel;
